import {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Search,
  X,
  ArrowUpDown,
  RotateCw,
  Rss,
  ChevronDown,
  Check,
  Tags,
  Layers,
  Star,
  Sparkles,
  FileSpreadsheet,
} from "lucide-react";
import {
  getProductFeed,
  getProductFlagsOrClassifications,
  getSources,
  getStats,
  getFeedViews,
  favoriteFeedSource,
  unfavoriteFeedSource,
  exportCatalog,
} from "../../../api/feed";
import { useArticleSidebar } from "../../Universal/ArticleCardDetailPanel";
import makeid from "../../Universal/Makeid";
import DateRangePicker from "../../Universal/DateRangePicker";

import { useDark } from "./feedTheme";
import { getTheme } from "./feedTheme";
import { cn, SORT_OPTIONS } from "./feedConstants";
import { TypeBadge, TargetBadge, AptBadge } from "./Badges";
import StatsBar from "./StatsBar";
import LensToolbar from "../../Universal/lens/LensToolbar";
import useLensTopbarVar from "../../Universal/lens/useLensTopbarVar";
import { FeedCard, ScrollSentinel } from "./FeedCard";
import TakedownCard from "./TakedownCard";
import TakedownShoppingCard from "./TakedownShoppingCard";
import CreateEditViewModal from "./CreateEditViewModal";

const QUICK_FILTERS = [
  { key: "ns", label: "No Signal", type: "confidence", value: "ns" },
  { key: "low", label: "Low", type: "confidence", value: "low" },
  { key: "medium", label: "Medium", type: "confidence", value: "med" },
  { key: "high", label: "High", type: "confidence", value: "high" },
  {
    key: "bookmarked",
    label: "Bookmarked",
    type: "bookmarked",
    value: "bookmarked",
  },
];

const CONTROL_TRIGGER_CLASS =
  "flex h-[26px] items-center gap-1.5 rounded-lg px-2 text-[11px] font-normal transition-all duration-200 whitespace-nowrap";

const formatClassificationLabel = (value) =>
  String(value ?? "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatFlagLabel = (value) =>
  String(value ?? "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase()
    .trim();

const getClassificationValue = (article) =>
  article?.classification || article?.target_type || article?.type || "";

const MASONRY_ROW_UNIT = 8;
const MASONRY_GAP = 16; // must match the grid's gap-4

// True masonry inside a single CSS grid: the grid uses fine-grained
// auto-rows, and each card measures its content and spans just enough of
// them. Heights pack tightly instead of every grid row inheriting its
// tallest card, and `dense` flow backfills around the wide (2-track)
// ecommerce cards. Height changes (history expand, read-more) re-measure
// automatically via ResizeObserver.
const MasonryItem = ({ wide, columnCount, animationDelay, children }) => {
  const contentRef = useRef(null);
  const [rowSpan, setRowSpan] = useState(1);

  useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return undefined;

    const measure = () => {
      const height = node.getBoundingClientRect().height;
      setRowSpan(
        Math.max(
          1,
          Math.ceil((height + MASONRY_GAP) / (MASONRY_ROW_UNIT + MASONRY_GAP)),
        ),
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="feed-card-in min-w-0"
      style={{
        animation: "feedCardIn 360ms ease-out both",
        animationDelay,
        gridColumn: wide ? `span ${Math.min(2, columnCount)}` : undefined,
        gridRowEnd: `span ${rowSpan}`,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

const getOptionKey = (item) => {
  if (typeof item === "string") return item;
  if (typeof item === "number") return String(item);

  return (
    item?.label ||
    item?.name ||
    item?.flag ||
    item?.flags ||
    item?.classification ||
    item?.value ||
    item?.key ||
    item?._id ||
    ""
  );
};

const getOptionLabel = (item, formatter) => {
  if (typeof item === "string" || typeof item === "number") {
    return formatter(String(item));
  }

  if (item?.label) return item.label;

  const fallback = getOptionKey(item);
  return fallback ? formatter(fallback) : "";
};

const unwrapOptionResponse = (response) => {
  const data = response?.data ?? response ?? [];

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.values)) return data.values;

  return [];
};

const buildFilterOptions = (values, formatter) =>
  [...values]
    .map((item) => {
      const key = getOptionKey(item);
      const label = getOptionLabel(item, formatter);

      if (!key || !label) return null;

      return {
        key,
        label,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));

const formatStatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const numeric = Number(value);

  if (!Number.isNaN(numeric) && String(value).trim() !== "") {
    return new Intl.NumberFormat().format(numeric);
  }

  return String(value);
};

const LoadingDots = ({ t }) => (
  <span
    className="inline-flex w-5 items-center justify-start pl-0.5"
    aria-hidden="true"
  >
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="opacity-0"
        style={{
          color: t.activeText,
          animation: "postLoadingDotFade 1.25s ease-in-out infinite",
          animationDelay: `${i * 180}ms`,
        }}
      >
        .
      </span>
    ))}
  </span>
);

const LoadingStat = ({ label, value, t }) => {
  const hasValue = value !== "—";

  return (
    <div
      className="min-w-0 rounded-lg px-2.5 py-2 text-center transition-colors duration-300"
      style={{
        background: t.inputBg,
        border: `1px solid ${t.inputBorder}`,
      }}
    >
      <div
        key={value}
        className="truncate text-[13px] leading-none tracking-wide tabular-nums"
        style={{
          color: hasValue ? t.textPrimary : t.textMuted,
          animation: hasValue ? "postLoadingValueIn 220ms ease-out" : "none",
        }}
      >
        {value}
      </div>

      <div
        className="mt-1 text-[9px] uppercase tracking-[0.16em]"
        style={{ color: t.textMuted }}
      >
        {label}
      </div>
    </div>
  );
};

const LoadingStatusPill = ({ icon: Icon, label, done, t }) => (
  <div
    className="flex min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] transition-colors duration-300"
    style={{
      background: t.inputBg,
      border: `1px solid ${done ? t.activeBorder : t.inputBorder}`,
      color: done ? t.activeText : t.textMuted,
    }}
  >
    <span
      key={done ? "done" : "pending"}
      className="inline-flex shrink-0 items-center justify-center"
      style={{
        animation: done ? "postLoadingValueIn 180ms ease-out" : "none",
      }}
    >
      {done ? (
        <Check size={11} />
      ) : (
        <Icon size={11} style={{ opacity: 0.68 }} />
      )}
    </span>

    <span className="truncate">{label}</span>
  </div>
);

const PostsLoadingState = ({ progress, stats, t }) => {
  const total = formatStatValue(stats?.total);
  const sources = formatStatValue(stats?.sources);
  const types = formatStatValue(stats?.types);

  return (
    <div className="flex min-h-[360px] w-full items-start justify-center px-4 pt-[72px]">
      <style>
        {`
          @keyframes postLoadingDotFade {
            0%, 20% { opacity: 0; }
            35%, 70% { opacity: 1; }
            85%, 100% { opacity: 0; }
          }

          @keyframes postLoadingValueIn {
            0% {
              opacity: 0;
              transform: translateY(2px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div
        className="relative w-full max-w-[460px] overflow-hidden rounded-xl text-center backdrop-blur-sm"
        style={{
          background: t.barBg,
          border: `1px solid ${t.barBorder}`,
          boxShadow: t.barShadow,
        }}
      >
        <div className="flex flex-col px-4 py-4">
          <div className="min-w-0 text-left">
            <div
              className="flex items-center text-[13px] font-normal tracking-wide"
              style={{ color: t.textPrimary }}
            >
              Loading posts
              <LoadingDots t={t} />
            </div>

            <p
              className="mt-1 text-[11px] leading-relaxed"
              style={{ color: t.textMuted }}
            >
              Preparing feed data, sources, classifications, and flags.
            </p>
          </div>

          <div
            className="my-3 h-px w-full"
            style={{ background: t.divider, opacity: 0.5 }}
          />

          <div className="grid w-full grid-cols-3 gap-2">
            <LoadingStat label="Results" value={total} t={t} />
            <LoadingStat label="Sources" value={sources} t={t} />
            <LoadingStat label="Types" value={types} t={t} />
          </div>

          <div className="mt-2 grid w-full grid-cols-3 gap-2">
            <LoadingStatusPill
              icon={Rss}
              label="Sources"
              done={progress?.sources}
              t={t}
            />

            <LoadingStatusPill
              icon={Layers}
              label="Classes"
              done={progress?.classifications}
              t={t}
            />

            <LoadingStatusPill
              icon={Tags}
              label="Flags"
              done={progress?.flags}
              t={t}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Lightweight loader shown on refetches (filter / sort / search / refresh).
// Mirrors PostsLoadingState's card + header, minus the stats grid and progress
// pills, so the two states feel like the same family. Re-declares the dot
// keyframes since PostsLoadingState (which owns them) isn't mounted here.
const FeedRefetchingState = ({ t }) => (
  <div className="flex min-h-[200px] w-full items-start justify-center px-4 pt-[72px]">
    <style>
      {`
        @keyframes postLoadingDotFade {
          0%, 20% { opacity: 0; }
          35%, 70% { opacity: 1; }
          85%, 100% { opacity: 0; }
        }
      `}
    </style>

    <div
      className="relative w-full max-w-[460px] overflow-hidden rounded-xl text-center backdrop-blur-sm"
      style={{
        background: t.barBg,
        border: `1px solid ${t.barBorder}`,
        boxShadow: t.barShadow,
      }}
    >
      <div className="flex flex-col px-4 py-4">
        <div className="min-w-0 text-left">
          <div
            className="flex items-center text-[13px] font-normal tracking-wide"
            style={{ color: t.textPrimary }}
          >
            Loading posts
            <LoadingDots t={t} />
          </div>

          <p
            className="mt-1 text-[11px] leading-relaxed"
            style={{ color: t.textMuted }}
          >
            Updating your feed.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const DisabledControl = ({ disabled, title, className = "", children }) => (
  <div
    className={cn(
      className,
      "transition-opacity",
      disabled && "pointer-events-none opacity-45",
    )}
    title={disabled ? title : undefined}
  >
    {children}
  </div>
);

const Divider = ({ t, vertical = true }) => (
  <div
    aria-hidden="true"
    style={{
      width: vertical ? 1 : "100%",
      height: vertical ? 18 : 1,
      background: t.divider,
      opacity: 0.55,
      flexShrink: 0,
    }}
  />
);

const IconBtn = ({ title, onClick, t, children, disabled = false }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-lg transition-all"
    style={{
      background: t.inputBg,
      border: `1px solid ${t.inputBorder}`,
      color: t.textSecondary,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.45 : 1,
    }}
    onMouseEnter={(event) => {
      if (disabled) return;
      event.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
      event.currentTarget.style.boxShadow = t.hoverBtnShadow;
    }}
    onMouseLeave={(event) => {
      if (disabled) return;
      event.currentTarget.style.border = `1px solid ${t.inputBorder}`;
      event.currentTarget.style.boxShadow = "none";
    }}
    onClick={onClick}
  >
    {children}
  </button>
);

// Secondary text button (icon + label). Same box as QuickPill (px-2 py-1,
// text-xs, 1px border) so it lines up exactly with "Bookmarked", but the
// border is transparent and there's no fill until hover, so it reads as an
// action rather than another filter.
const ExportButton = ({ onClick, busy, t }) => (
  <button
    type="button"
    title="Export your bookmarked catalog as a CSV file"
    disabled={busy}
    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs whitespace-nowrap transition-all duration-200 flex-shrink-0"
    style={{
      background: "transparent",
      border: "1px solid transparent",
      color: t.textSecondary,
      cursor: busy ? "default" : "pointer",
      opacity: busy ? 0.6 : 1,
    }}
    onMouseEnter={(e) => {
      if (busy) return;
      e.currentTarget.style.background = t.hoverBg;
      e.currentTarget.style.color = t.activeText;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.color = t.textSecondary;
    }}
    onClick={onClick}
  >
    <FileSpreadsheet size={12} style={{ opacity: 0.75, flexShrink: 0 }} />
    <span style={{ fontSize: 11, fontWeight: 400 }}>
      {busy ? "Exporting…" : "Export"}
    </span>
  </button>
);

const QuickPill = ({ active, children, onClick, t }) => (
  <button
    className="flex items-center px-2 py-1 rounded-lg text-xs whitespace-nowrap transition-all duration-200 flex-shrink-0"
    style={{
      background: active ? t.activeBg : t.pillBg,
      border: active
        ? `1px solid ${t.activeBorder}`
        : `1px solid ${t.pillBorder}`,
    }}
    onMouseEnter={(e) => {
      if (!active) {
        e.currentTarget.style.background = t.hoverBg;
        e.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
        e.currentTarget.style.boxShadow = t.hoverShadow;
      }
    }}
    onMouseLeave={(e) => {
      if (!active) {
        e.currentTarget.style.background = t.pillBg;
        e.currentTarget.style.border = `1px solid ${t.pillBorder}`;
        e.currentTarget.style.boxShadow = "none";
      }
    }}
    onClick={onClick}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 400,
        color: active ? t.activeText : t.pillText,
      }}
    >
      {children}
    </span>
  </button>
);

const ControlDropdown = ({
  isOpen,
  onClose,
  trigger,
  children,
  t,
  width = 240,
}) => {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      onClose?.();
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      {trigger}

      {isOpen && (
        <div
          className="absolute left-0 top-full z-[9999] mt-2 overflow-hidden rounded-xl backdrop-blur-md"
          style={{
            width,
            background: t.menuBg || t.barBg,
            border: `1px solid ${t.menuBorder || t.barBorder}`,
            boxShadow: t.menuShadow || t.barShadow,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const ControlTrigger = ({
  label,
  icon: Icon,
  active,
  open,
  t,
  onClick,
  minWidth = 104,
  count,
  onClear,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={CONTROL_TRIGGER_CLASS}
    style={{
      background: active ? t.activeBg : t.inputBg,
      border: active
        ? `1px solid ${t.activeBorder}`
        : `1px solid ${t.inputBorder}`,
      color: active ? t.activeText : t.textSecondary,
      cursor: "pointer",
      minWidth,
      fontSize: 11,
      fontWeight: 400,
      lineHeight: 1,
    }}
    onMouseEnter={(e) => {
      if (!active) {
        e.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
        e.currentTarget.style.boxShadow = t.hoverBtnShadow;
      }
    }}
    onMouseLeave={(e) => {
      if (!active) {
        e.currentTarget.style.border = `1px solid ${t.inputBorder}`;
        e.currentTarget.style.boxShadow = "none";
      }
    }}
  >
    {Icon && <Icon size={13} style={{ opacity: 0.6, flexShrink: 0 }} />}

    <span className="truncate flex-1 text-left font-normal">{label}</span>

    {count > 0 && (
      <span
        className="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-normal"
        style={{
          background: t.activeBorder,
          color: t.activeText,
          lineHeight: 1,
        }}
      >
        {count}
      </span>
    )}

    {count > 0 && onClear && (
      <span
        role="button"
        tabIndex={-1}
        className="inline-flex items-center justify-center opacity-45 transition-opacity hover:opacity-90"
        onClick={(event) => {
          event.stopPropagation();
          onClear();
        }}
      >
        <X size={12} />
      </span>
    )}

    <ChevronDown
      size={13}
      style={{
        opacity: 0.4,
        flexShrink: 0,
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.15s ease",
      }}
    />
  </button>
);

const SingleSelectDropdown = ({
  isOpen,
  onClose,
  onToggle,
  value,
  options,
  icon,
  t,
  minWidth = 112,
  menuWidth = 190,
  onChange,
}) => {
  const selected = options.find((option) => option.key === value) || options[0];

  return (
    <ControlDropdown
      isOpen={isOpen}
      onClose={onClose}
      t={t}
      width={menuWidth}
      trigger={
        <ControlTrigger
          label={selected?.label || "Select"}
          icon={icon}
          active={value !== "newest"}
          open={isOpen}
          t={t}
          minWidth={minWidth}
          onClick={onToggle}
        />
      }
    >
      <div className="p-1">
        {options.map((option) => {
          const active = option.key === value;

          return (
            <button
              key={option.key}
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[11px] font-normal transition-all"
              style={{
                background: active ? t.activeBg : "transparent",
                color: active ? t.activeText : t.textSecondary,
              }}
              onMouseEnter={(event) => {
                if (!active) event.currentTarget.style.background = t.hoverBg;
              }}
              onMouseLeave={(event) => {
                if (!active) {
                  event.currentTarget.style.background = "transparent";
                }
              }}
              onClick={() => {
                onChange(option.key);
                onClose();
              }}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {active && <Check size={13} style={{ flexShrink: 0 }} />}
            </button>
          );
        })}
      </div>
    </ControlDropdown>
  );
};

const getSelectedLabel = (selected, options, label) => {
  if (!selected || selected.size === 0) return label;

  if (selected.size === 1) {
    const key = [...selected][0];
    const match = options.find((option) => option.key === key);
    return match?.label || key;
  }

  return `${label} (${selected.size})`;
};

const MultiFilterDropdown = ({
  isOpen,
  onClose,
  onToggle,
  selected,
  options = [],
  searchValue = "",
  onSearchChange,
  label,
  icon,
  t,
  minWidth = 104,
  menuWidth = 280,
  favoritedSources = [],
  onToggleFavorite,
  onChange,
  single = false,
}) => {
  const selectedLabel = getSelectedLabel(selected, options, label);
  const hasSelection = selected?.size > 0;

  const filteredOptions = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return options;

    return options.filter((option) =>
      String(option.label || option.key)
        .toLowerCase()
        .includes(q),
    );
  }, [options, searchValue]);

  const updateSelection = (key) => {
    if (single) {
      if (selected?.has(key)) onChange(new Set(), key);
      else onChange(new Set([key]), key);
      return;
    }

    const next = new Set(selected || []);

    if (next.has(key)) next.delete(key);
    else next.add(key);

    onChange(next, key);
  };

  const isFavorited = (key) =>
    favoritedSources.some((source) => source.source_value === key);

  return (
    <ControlDropdown
      isOpen={isOpen}
      onClose={onClose}
      t={t}
      width={menuWidth}
      trigger={
        <ControlTrigger
          label={selectedLabel}
          icon={icon}
          active={hasSelection}
          open={isOpen}
          t={t}
          minWidth={minWidth}
          count={selected?.size || 0}
          onClear={() => onChange(new Set())}
          onClick={onToggle}
        />
      }
    >
      <div className="p-2">
        <div className="relative">
          <Search
            size={13}
            style={{
              position: "absolute",
              left: 9,
              top: "50%",
              transform: "translateY(-50%)",
              color: t.textMuted,
              opacity: 0.65,
            }}
          />

          <input
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder={`Search ${String(label || "").toLowerCase()}`}
            className="h-[28px] w-full rounded-lg pl-7 pr-2 text-[11px] font-normal outline-none placeholder:opacity-80"
            style={{
              background: t.inputBg,
              border: `1px solid ${t.inputBorder}`,
              color: t.inputText,
            }}
          />
        </div>
      </div>

      <div
        className="max-h-[300px] overflow-y-auto px-1 pb-1"
        style={{ scrollbarWidth: "thin" }}
      >
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-3 text-[11px]" style={{ color: t.textMuted }}>
            No options found
          </div>
        ) : (
          filteredOptions.map((option) => {
            const checked = selected?.has(option.key);
            const favorite = isFavorited(option.key);

            return (
              <button
                key={option.key}
                type="button"
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-all"
                style={{
                  background: checked ? t.activeBg : "transparent",
                  color: checked ? t.activeText : t.textSecondary,
                }}
                onMouseEnter={(event) => {
                  if (!checked)
                    event.currentTarget.style.background = t.hoverBg;
                }}
                onMouseLeave={(event) => {
                  if (!checked) {
                    event.currentTarget.style.background = "transparent";
                  }
                }}
                onClick={() => updateSelection(option.key)}
              >
                <span
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded"
                  style={{
                    border: `1px solid ${
                      checked ? t.activeBorder : t.inputBorder
                    }`,
                    background: checked ? t.activeBg : t.inputBg,
                  }}
                >
                  {checked && <Check size={11} />}
                </span>

                <span className="min-w-0 flex-1 truncate text-[11px] font-normal">
                  {option.label}
                </span>

                {onToggleFavorite && (
                  <span
                    role="button"
                    tabIndex={-1}
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100"
                    style={{
                      color: favorite ? t.activeText : t.textMuted,
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleFavorite(option);
                    }}
                  >
                    <Star size={13} fill={favorite ? "currentColor" : "none"} />
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </ControlDropdown>
  );
};

const MainFeeds = () => {
  const dark = useDark();
  const t = getTheme(dark);

  const [systemViews, setSystemViews] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [favoritedSources, setFavoritedSources] = useState([]);
  const [activeView, setActiveView] = useState(null);

  const [showCreateViewModal, setShowCreateViewModal] = useState(false);
  const [editingView, setEditingView] = useState(null);

  const activeViewRef = useRef(null);
  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  const [articleCards, setArticleCards] = useState([]);
  const [feedOffset, setFeedOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadedFeedOnce, setHasLoadedFeedOnce] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState("newest");
  const [selectedQuickFilters, setSelectedQuickFilters] = useState(new Set());
  const [sortOpen, setSortOpen] = useState(false);

  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [selectedSources, setSelectedSources] = useState(new Set());
  const [sourceSearch, setSourceSearch] = useState("");

  const [classificationsOpen, setClassificationsOpen] = useState(false);
  const [selectedClassifications, setSelectedClassifications] = useState(
    new Set(),
  );
  const [classificationSearch, setClassificationSearch] = useState("");

  const [flagsOpen, setFlagsOpen] = useState(false);
  const [selectedFlags, setSelectedFlags] = useState(new Set());
  const [flagSearch, setFlagSearch] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [feedStats, setFeedStats] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);
  const [availableClassifications, setAvailableClassifications] = useState([]);
  const [availableFlags, setAvailableFlags] = useState([]);
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState({
    sources: false,
    classifications: false,
    flags: false,
  });

  const controlBarRef = useRef(null);
  // Publish this bar's measured height to --lens-topbar-h/-inset (the same
  // contract as LensShell's bar and ThreadsTopBar — one publisher per route):
  // the right aside and the sidebar toggle tile read these vars.
  useLensTopbarVar(controlBarRef);
  const searchInputRef = useRef(null);
  const feedScrollRef = useRef(null);

  useEffect(() => {
    if (!controlBarRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setBarHeight(entry.contentRect.height);
    });

    observer.observe(controlBarRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasLoadedFeedOnce) return undefined;

    const timer = window.setTimeout(() => {
      setAppliedSearchQuery(searchQuery.trim());
    }, 400);

    return () => window.clearTimeout(timer);
  }, [searchQuery, hasLoadedFeedOnce]);

  const allTargetTypes = useMemo(() => {
    const types = new Set();

    systemViews.forEach((v) => {
      (v.target_types || []).forEach((tt) => types.add(tt));
    });

    return [...types];
  }, [systemViews]);

  const selectedConfidenceFilter = useMemo(() => {
    const selectedKey = [...selectedQuickFilters].find((key) => {
      const filter = QUICK_FILTERS.find((item) => item.key === key);
      return filter?.type === "confidence";
    });

    if (!selectedKey) return null;

    return QUICK_FILTERS.find((item) => item.key === selectedKey) || null;
  }, [selectedQuickFilters]);

  const bookmarkedFilterActive = selectedQuickFilters.has("bookmarked");

  const feedControlsReady = hasLoadedFeedOnce && !isLoading;

  // First load of the component's lifetime → rich loader. Filter options
  // (sources / classifications / flags) are also loading at this point, and
  // they only ever load once, so the progress pills are only meaningful here.
  const showInitialFeedLoading =
    !activeView || (isLoading && !hasLoadedFeedOnce);

  // We've already rendered the feed at least once and are fetching again
  // (filter / sort / search / refresh / view switch) → lightweight loader.
  const isRefetching = isLoading && hasLoadedFeedOnce;

  const classificationFiltersReady =
    feedControlsReady &&
    filterOptionsLoaded &&
    availableClassifications.length > 0;
  const flagFiltersReady =
    feedControlsReady && filterOptionsLoaded && availableFlags.length > 0;
  const sourceFiltersReady = feedControlsReady && availableSources.length > 0;

  // Bookmarked is filtered server-side (payload.filter = "cataloged"), so the
  // cards from the API are already the right set.
  const filteredArticleCards = articleCards;

  // Dense grid (2026-08 restyle, supersedes the round-robin flex columns):
  // narrow base tracks; ecommerce cards (confidence meter overlay needs the
  // width) span two tracks, everything else takes one. `grid-auto-flow:
  // dense` backfills the holes the mixed spans leave. Cards stay in ONE
  // parent, so a track-count change is pure reflow — the old per-column
  // divs REPARENTED every card on resize, remounting them all and replaying
  // every entry animation (the "resizes all the time" jank).
  const feedColumnsContainerRef = useRef(null);
  const COLUMN_MIN_WIDTH = 300;
  const COLUMN_GAP = 16; // matches gap-4
  const columnsForWidth = (width) => {
    const raw = Math.max(
      1,
      Math.floor((width + COLUMN_GAP) / (COLUMN_MIN_WIDTH + COLUMN_GAP)),
    );
    // Even track counts only (or 1): ecommerce cards span 2 tracks, so an odd
    // count strands a permanent empty track on ecommerce-heavy feeds — the
    // "card fills 2/3 of the row" look.
    return raw <= 1 ? 1 : raw - (raw % 2);
  };
  // Seed from the viewport so the first paint is already multi-column — the
  // old useState(1) painted single-column and visibly snapped a frame later.
  const [feedColumnCount, setFeedColumnCount] = useState(() =>
    columnsForWidth(Math.max(0, (window.innerWidth || 1280) - 400)),
  );
  const hasFeedCards = filteredArticleCards.length > 0;

  useLayoutEffect(() => {
    const node = feedColumnsContainerRef.current;
    if (!node) return undefined;

    const update = () => setFeedColumnCount(columnsForWidth(node.clientWidth));

    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFeedCards]);

  const isWideFeedCard = (article) => article?.target_type === "ecommerce";

  const loadFeedViews = useCallback(async () => {
    try {
      const res = await getFeedViews();
      const d = res?.data || {};

      if (d.system_views) setSystemViews(d.system_views);
      if (d.custom_views) setCustomViews(d.custom_views);
      if (d.favorited_sources) setFavoritedSources(d.favorited_sources);

      if (!activeViewRef.current && d.system_views?.length) {
        const globalView = d.system_views.find((v) => v.key === "Global");

        if (globalView) {
          setActiveView({ kind: "system", key: "Global", view: globalView });
        }
      }
    } catch (e) {
      console.error("[FeedViews] Error:", e);
    }
  }, []);

  useEffect(() => {
    loadFeedViews();

    const handler = () => loadFeedViews();
    window.addEventListener("sidebar:refresh-feed-views", handler);

    return () =>
      window.removeEventListener("sidebar:refresh-feed-views", handler);
  }, [loadFeedViews]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;
      window.__pendingFeedViewSwitch = null;

      if (detail.kind === "system") {
        setActiveView({
          kind: "system",
          key: detail.view.key,
          view: detail.view,
        });
        setSelectedSources(new Set());
      } else if (detail.kind === "custom") {
        setActiveView({
          kind: "custom",
          document_id: detail.view.document_id,
          view: detail.view,
        });
        setSelectedSources(new Set());
      } else if (detail.kind === "favorited_source") {
        setActiveView({
          kind: "favorited_source",
          document_id: detail.document_id,
          source: detail.source,
        });
        setSelectedSources(new Set([detail.source]));
      }

      setSearchQuery("");
      setAppliedSearchQuery("");
      setSelectedQuickFilters(new Set());
      setSelectedClassifications(new Set());
      setSelectedFlags(new Set());
      setClassificationSearch("");
      setFlagSearch("");
    };

    window.addEventListener("sidebar:feed-switch-view", handler);
    // Replay a switch requested before this page mounted (sidebar click from
    // another route): the portal stashes the detail alongside the event.
    if (window.__pendingFeedViewSwitch) {
      handler({ detail: window.__pendingFeedViewSwitch });
    }
    return () =>
      window.removeEventListener("sidebar:feed-switch-view", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      setEditingView(null);
      setShowCreateViewModal(true);
    };

    window.addEventListener("sidebar:feed-open-create-modal", handler);
    return () =>
      window.removeEventListener("sidebar:feed-open-create-modal", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const view = e.detail?.view;
      if (!view) return;

      setEditingView(view);
      setShowCreateViewModal(true);
    };

    window.addEventListener("sidebar:feed-open-edit-modal", handler);
    return () =>
      window.removeEventListener("sidebar:feed-open-edit-modal", handler);
  }, []);

  useEffect(() => {
    if (activeView) {
      window.dispatchEvent(
        new CustomEvent("feed:active-view-changed", { detail: activeView }),
      );
    }
  }, [activeView]);


  const hasActiveFilter =
    activeSort !== "newest" ||
    searchQuery.trim() !== "" ||
    selectedSources.size > 0 ||
    selectedClassifications.size > 0 ||
    selectedFlags.size > 0 ||
    !!selectedConfidenceFilter ||
    bookmarkedFilterActive ||
    dateFrom ||
    dateTo;

  const buildCardEntries = (data) =>
    data.map((article) => ({
      id: article.document_id || article.id || makeid(12),
      article,
    }));

  const handleInteract = useCallback((cardId, docId, state) => {
    setArticleCards((prev) =>
      prev.map((entry) =>
        entry.article.document_id === docId || entry.id === cardId
          ? {
              ...entry,
              article: {
                ...entry.article,
                ...state,
              },
            }
          : entry,
      ),
    );
  }, []);

  const { activeCardId, setActiveCardId, handleCardClick } = useArticleSidebar(
    articleCards,
    handleInteract,
  );

  const buildPayload = useCallback(
    (offset) => {
      const payload = { offset };
      payload.group = "public";

      if (activeView?.kind === "system") {
        const v = activeView.view;

        if (v.uuid_scoped) payload.uuid = true;
        if (v.target_types?.length) payload.target_types = v.target_types;
        if (v.keywords?.length) payload.keywords = v.keywords;
      } else if (activeView?.kind === "custom") {
        payload.view_document_id = activeView.view.document_id;
      } else if (activeView?.kind === "favorited_source") {
        const globalView = systemViews.find((v) => v.key === "Global");

        if (globalView?.target_types?.length) {
          payload.target_types = globalView.target_types;
        }

        payload.domains = [activeView.source];
      }

      payload.sort = activeSort === "oldest" ? 1 : -1;

      if (selectedSources.size > 0 && activeView?.kind !== "favorited_source") {
        payload.domains = [...selectedSources];
      }

      if (selectedConfidenceFilter?.value) {
        payload.tier = selectedConfidenceFilter.value;
      }

      if (bookmarkedFilterActive) {
        payload.filter = "cataloged";
      }

      if (selectedFlags.size > 0) {
        payload.flags = [...selectedFlags];
      }

      if (selectedClassifications.size > 0) {
        payload.classification = [...selectedClassifications][0];
      }

      if (appliedSearchQuery) payload.search = appliedSearchQuery;
      if (dateFrom) payload.date_from = dateFrom;
      if (dateTo) payload.date_to = dateTo;

      return payload;
    },
    [
      activeView,
      activeSort,
      selectedSources,
      selectedFlags,
      selectedClassifications,
      selectedConfidenceFilter,
      bookmarkedFilterActive,
      appliedSearchQuery,
      dateFrom,
      dateTo,
      systemViews,
    ],
  );

  const fetchPage = useCallback(
    async (offset, append = false) => {
      if (!append) setIsLoading(true);
      else setIsLoadingMore(true);

      try {
        const payload = buildPayload(offset);
        console.log("[API] getProductFeed", payload);

        const response = await getProductFeed(payload);
        const data = response?.data ?? [];
        const entries = buildCardEntries(data);

        if (append) setArticleCards((prev) => [...prev, ...entries]);
        else setArticleCards(entries);

        setHasMore(data.length !== 0);
        setHasLoadedFeedOnce(true);
      } catch (e) {
        console.error("[Feed] Error:", e);
        setArticleCards([]);
        setHasMore(false);
        setHasLoadedFeedOnce(true);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [buildPayload],
  );

  useEffect(() => {
    const loadSources = async () => {
      setLoadingProgress((prev) => ({
        ...prev,
        sources: false,
      }));

      try {
        const response = await getSources();
        const data = response?.data ?? response ?? [];

        const mapped = data.map((s) => ({
          key: s.label,
          label: s.label,
        }));

        setAvailableSources(mapped);
      } catch (e) {
        console.error("[Sources] Error loading sources:", e);
        setAvailableSources([]);
      } finally {
        setLoadingProgress((prev) => ({
          ...prev,
          sources: true,
        }));
      }
    };

    loadSources();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProductFilterOptions = async () => {
      setFilterOptionsLoaded(false);
      setLoadingProgress((prev) => ({
        ...prev,
        classifications: false,
        flags: false,
      }));

      const flagsPromise = getProductFlagsOrClassifications("flags")
        .then((response) => {
          if (cancelled) return;

          setAvailableFlags(
            buildFilterOptions(unwrapOptionResponse(response), formatFlagLabel),
          );
        })
        .catch((e) => {
          console.error("[ProductFeedFilters] Error loading flags:", e);

          if (!cancelled) {
            setAvailableFlags([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingProgress((prev) => ({
              ...prev,
              flags: true,
            }));
          }
        });

      const classificationsPromise = getProductFlagsOrClassifications(
        "classification",
      )
        .then((response) => {
          if (cancelled) return;

          setAvailableClassifications(
            buildFilterOptions(
              unwrapOptionResponse(response),
              formatClassificationLabel,
            ),
          );
        })
        .catch((e) => {
          console.error(
            "[ProductFeedFilters] Error loading classifications:",
            e,
          );

          if (!cancelled) {
            setAvailableClassifications([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingProgress((prev) => ({
              ...prev,
              classifications: true,
            }));
          }
        });

      await Promise.allSettled([flagsPromise, classificationsPromise]);

      if (!cancelled) {
        setFilterOptionsLoaded(true);
      }
    };

    loadProductFilterOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeView) return;

    setArticleCards([]);
    setFeedOffset(0);
    setHasMore(true);
    setActiveCardId(null);
    fetchPage(0, false);

    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  }, [activeView, fetchPage, setActiveCardId]);

  useEffect(() => {
    if (!activeView) return;

    const loadStats = async () => {
      try {
        const payload = buildPayload(0);
        delete payload.offset;

        console.log("[API] getStats", payload);

        const response = await getStats(payload);
        const data = response?.data ?? response;

        if (data && data.total != null) setFeedStats(data);
        else setFeedStats(null);
      } catch (e) {
        console.error("[Stats] Error (will use local fallback):", e);
        setFeedStats(null);
      }
    };

    loadStats();
  }, [activeView, buildPayload]);

  const effectiveStats =
    feedStats ||
    (() => {
      if (articleCards.length === 0) return null;

      const sources = new Set();
      const types = new Set();
      const timestamps = [];

      articleCards.forEach(({ article }) => {
        try {
          sources.add(new URL(article.url).hostname.replace("www.", ""));
        } catch {}

        const tt = getClassificationValue(article);
        if (tt) types.add(tt);

        const ts = article.date || article.timestamp;
        if (ts) {
          timestamps.push(
            typeof ts === "number" ? ts * 1000 : new Date(ts).getTime(),
          );
        }
      });

      const validTs = timestamps.filter((n) => !Number.isNaN(n));

      return {
        total:
          filteredArticleCards.length + (hasMore ? "+" : ""),
        sources: sources.size,
        types: types.size,
        date_from: validTs.length
          ? new Date(Math.min(...validTs)).toISOString()
          : null,
        date_to: validTs.length
          ? new Date(Math.max(...validTs)).toISOString()
          : null,
      };
    })();

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || !feedControlsReady) return;

    const next = feedOffset + 1;
    setFeedOffset(next);
    fetchPage(next, true);
  }, [feedOffset, isLoadingMore, hasMore, fetchPage, feedControlsReady]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();

    if (!feedControlsReady) return;

    setAppliedSearchQuery(searchQuery.trim());
  };

  const clearSearch = () => {
    if (!feedControlsReady) return;

    setSearchQuery("");
    setAppliedSearchQuery("");

    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  };

  const handleRefresh = () => {
    if (!feedControlsReady) return;

    setArticleCards([]);
    setFeedOffset(0);
    setHasMore(true);
    fetchPage(0, false);

    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  };

  // Export the user's bookmarked posts. The API does all the work and returns
  // CSV (url,title,source,content,description); we just hand it to the browser
  // as a file download. Accepts a Blob or a plain string from the API helper.
  const handleExportCatalog = async () => {
    if (isExporting) return;

    setIsExporting(true);

    try {
      const response = await exportCatalog();
      const data = response?.data ?? response;

      // BOM so Excel reads the UTF-8 correctly (accents, curly quotes, emoji).
      const blob = new Blob(["\uFEFF", data], {
        type: "text/csv;charset=utf-8",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bookmarked-posts-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[ExportCatalog] Error:", e);
    } finally {
      setIsExporting(false);
    }
  };

  const clearAllFilters = () => {
    if (!feedControlsReady) return;

    setActiveSort("newest");
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSelectedSources(new Set());
    setSelectedQuickFilters(new Set());
    setSelectedClassifications(new Set());
    setSelectedFlags(new Set());
    setClassificationSearch("");
    setFlagSearch("");
    setDateFrom("");
    setDateTo("");

    const globalView = systemViews.find((v) => v.key === "Global");

    if (globalView && activeView?.key !== "Global") {
      setActiveView({ kind: "system", key: "Global", view: globalView });
    }
  };

  const handleSourcesChanged = (newSources) => {
    if (!sourceFiltersReady) return;

    setSelectedSources(newSources);

    if (activeView?.kind === "favorited_source") {
      const globalView = systemViews.find((v) => v.key === "Global");

      if (globalView) {
        setActiveView({ kind: "system", key: "Global", view: globalView });
      }
    }

    if (newSources.size === 1) {
      const singleSource = [...newSources][0];

      const matchingFav = favoritedSources.find(
        (f) => f.source_value === singleSource,
      );

      if (matchingFav) {
        setActiveView({
          kind: "favorited_source",
          document_id: matchingFav.document_id,
          source: matchingFav.source_value,
        });
      }
    }
  };

  const handleClassificationChange = (next) => {
    setSelectedClassifications(next);

    if (next.size <= 1) {
      setClassificationsOpen(false);
      setClassificationSearch("");
    }
  };

  const handleToggleFavoriteSource = async (source) => {
    const existing = favoritedSources.find(
      (f) => f.source_value === source.key,
    );

    try {
      if (existing) {
        await unfavoriteFeedSource({ document_id: existing.document_id });
      } else {
        await favoriteFeedSource({
          source_value: source.key,
          source_label: source.label,
        });
      }

      loadFeedViews();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    } catch (err) {
      console.error("Toggle favorite source failed:", err);
    }
  };

  const toggleQuickFilter = (key) => {
    if (!feedControlsReady) return;

    if (key === "all") {
      setSelectedQuickFilters(new Set());
      return;
    }

    const clickedFilter = QUICK_FILTERS.find((filter) => filter.key === key);

    setSelectedQuickFilters((prev) => {
      const next = new Set(prev);

      if (clickedFilter?.type === "confidence") {
        QUICK_FILTERS.filter((filter) => filter.type === "confidence").forEach(
          (filter) => next.delete(filter.key),
        );

        if (!prev.has(key)) next.add(key);

        return next;
      }

      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });
  };

  const handleModalSaved = (view) => {
    setShowCreateViewModal(false);
    setEditingView(null);
    loadFeedViews();
    window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));

    if (view) {
      setActiveView({
        kind: "custom",
        document_id: view.document_id,
        view,
      });
    }
  };

  const resetModal = () => {
    setShowCreateViewModal(false);
    setEditingView(null);
  };

  const activeViewLabel = useMemo(() => {
    if (!activeView) return "Feed";
    if (activeView.kind === "system") return activeView.key;
    if (activeView.kind === "custom") return activeView.view?.name || "Custom";
    if (activeView.kind === "favorited_source")
      return activeView.source || "Source";
    return "Feed";
  }, [activeView]);

  const confidenceFilters = QUICK_FILTERS.filter(
    (filter) => filter.type === "confidence",
  );
  const bookmarkFilter = QUICK_FILTERS.find(
    (filter) => filter.type === "bookmarked",
  );

  return (
    <div className="relative flex flex-col w-full h-full">
      {/* The feed control bar rides the LensToolbar FRAME: same glass, same
          top-3 line as the sidebar toggle tile, and the same sidebar-aware
          gutter math (left animates with the sidebar; right pinned 16px) —
          replacing the old absolute top-16 placement that assumed the
          removed navbar above it. controlBarRef still lands on the glass
          container, so the ResizeObserver height feed is unchanged. */}
      <LensToolbar ref={controlBarRef}>
        <div className="flex flex-col gap-2 overflow-visible">
          <div className="flex items-center gap-3">
            <DisabledControl
              disabled={!feedControlsReady}
              title="Search will be available after feed data loads"
              className="flex-1"
            >
              <form onSubmit={handleSearchSubmit} className="relative w-full">
                <Search
                  size={13}
                  style={{
                    position: "absolute",
                    left: 9,
                    top: "50%",
                    transform: "translateY(-50%)",
                    opacity: searchQuery ? 0.8 : 0.45,
                    color: searchQuery ? t.activeText : t.textSecondary,
                  }}
                />

                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  disabled={!feedControlsReady}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      handleSearchSubmit(e);
                    }

                    if (e.key === "Escape") clearSearch();
                  }}
                  placeholder={`Search ${activeViewLabel.toLowerCase()} feed view`}
                  className="h-[26px] w-full rounded-lg pl-7 pr-7 text-[11px] font-normal outline-none transition-colors placeholder:opacity-80"
                  style={{
                    background: t.inputBg,
                    border: searchQuery
                      ? `1px solid ${t.hoverBorder}`
                      : `1px solid ${t.inputBorder}`,
                    color: t.inputText,
                  }}
                />

                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-80 transition-opacity"
                    onClick={clearSearch}
                  >
                    <X size={13} />
                  </button>
                )}
              </form>
            </DisabledControl>
          </div>

          <div className="flex items-center gap-2 overflow-visible pb-0.5">
            <DisabledControl
              disabled={!feedControlsReady}
              title="Sort will be available after feed data loads"
            >
              <SingleSelectDropdown
                isOpen={sortOpen}
                onClose={() => setSortOpen(false)}
                onToggle={() => {
                  if (!feedControlsReady) return;
                  setSortOpen((v) => !v);
                  setSourcesOpen(false);
                  setClassificationsOpen(false);
                  setFlagsOpen(false);
                }}
                value={activeSort}
                options={SORT_OPTIONS}
                icon={ArrowUpDown}
                t={t}
                onChange={setActiveSort}
              />
            </DisabledControl>

            <Divider t={t} />

            <DisabledControl
              disabled={!sourceFiltersReady}
              title="Sources will be available after feed data loads"
            >
              <MultiFilterDropdown
                isOpen={sourcesOpen}
                onClose={() => {
                  setSourcesOpen(false);
                  setSourceSearch("");
                }}
                onToggle={() => {
                  if (!sourceFiltersReady) return;
                  setSourcesOpen((v) => !v);
                  setSortOpen(false);
                  setClassificationsOpen(false);
                  setFlagsOpen(false);
                  if (sourcesOpen) setSourceSearch("");
                }}
                selected={selectedSources}
                options={availableSources}
                searchValue={sourceSearch}
                onSearchChange={setSourceSearch}
                label="Sources"
                icon={Rss}
                t={t}
                favoritedSources={favoritedSources}
                onToggleFavorite={handleToggleFavoriteSource}
                onChange={handleSourcesChanged}
              />
            </DisabledControl>

            <Divider t={t} />

            <DisabledControl
              disabled={!classificationFiltersReady}
              title="Classifications will be available after feed data loads"
            >
              <MultiFilterDropdown
                isOpen={classificationsOpen}
                onClose={() => {
                  setClassificationsOpen(false);
                  setClassificationSearch("");
                }}
                onToggle={() => {
                  if (!classificationFiltersReady) return;
                  setClassificationsOpen((v) => !v);
                  setSortOpen(false);
                  setSourcesOpen(false);
                  setFlagsOpen(false);
                  if (classificationsOpen) setClassificationSearch("");
                }}
                selected={selectedClassifications}
                options={availableClassifications}
                searchValue={classificationSearch}
                onSearchChange={setClassificationSearch}
                label="Classifications"
                icon={Layers}
                t={t}
                minWidth={130}
                onChange={handleClassificationChange}
                single
              />
            </DisabledControl>

            <Divider t={t} />

            <DisabledControl
              disabled={!flagFiltersReady}
              title="Flags will be available after feed data loads"
            >
              <MultiFilterDropdown
                isOpen={flagsOpen}
                onClose={() => {
                  setFlagsOpen(false);
                  setFlagSearch("");
                }}
                onToggle={() => {
                  if (!flagFiltersReady) return;
                  setFlagsOpen((v) => !v);
                  setSortOpen(false);
                  setSourcesOpen(false);
                  setClassificationsOpen(false);
                  if (flagsOpen) setFlagSearch("");
                }}
                selected={selectedFlags}
                options={availableFlags}
                searchValue={flagSearch}
                onSearchChange={setFlagSearch}
                label="Flags"
                icon={Tags}
                t={t}
                onChange={setSelectedFlags}
              />
            </DisabledControl>

            <Divider t={t} />

            <DisabledControl
              disabled={!feedControlsReady}
              title="Date range will be available after feed data loads"
            >
              <DateRangePicker
                compact
                triggerClassName={CONTROL_TRIGGER_CLASS}
                inputClassName="h-[26px] px-2 py-0 text-[11px] font-normal"
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={(from, to) => {
                  if (!feedControlsReady) return;
                  setDateFrom(from);
                  setDateTo(to);
                }}
                t={t}
              />
            </DisabledControl>

            {hasActiveFilter && (
              <>
                <Divider t={t} />
                <button
                  className="flex h-[26px] items-center gap-1.5 rounded-lg px-2 text-[11px] font-normal transition-all whitespace-nowrap"
                  style={{
                    color: t.clearText,
                    background: t.clearBg,
                    border: `1px solid ${t.clearBorder}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = t.clearBgHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = t.clearBg;
                  }}
                  onClick={clearAllFilters}
                >
                  <X size={12} />
                  Clear
                </button>
              </>
            )}

            <div className="flex-1 min-w-2" />

            <DisabledControl
              disabled={!feedControlsReady}
              title="Refresh will be available after feed data loads"
            >
              <IconBtn title="Refresh" onClick={handleRefresh} t={t}>
                <RotateCw size={16} style={{ color: t.iconDefault }} />
              </IconBtn>
            </DisabledControl>
          </div>
        </div>

        <div style={{ height: 1, background: t.divider, opacity: 0.4 }} />

        <div className="flex items-center gap-1.5 pt-2 rounded-b-xl">
          <DisabledControl
            disabled={!feedControlsReady}
            title="Filters will be available after feed data loads"
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <QuickPill
                active={selectedQuickFilters.size === 0}
                onClick={() => toggleQuickFilter("all")}
                t={t}
              >
                All
              </QuickPill>

              <span
                className="mx-1 h-4 w-px"
                style={{ background: t.divider, opacity: 0.55 }}
              />

              {confidenceFilters.map((opt) => {
                const isActive = selectedQuickFilters.has(opt.key);

                return (
                  <QuickPill
                    key={opt.key}
                    active={isActive}
                    onClick={() => toggleQuickFilter(opt.key)}
                    t={t}
                  >
                    {opt.label}
                  </QuickPill>
                );
              })}

              <span
                className="mx-1 h-4 w-px"
                style={{ background: t.divider, opacity: 0.55 }}
              />

              {bookmarkFilter && (
                <QuickPill
                  active={selectedQuickFilters.has(bookmarkFilter.key)}
                  onClick={() => toggleQuickFilter(bookmarkFilter.key)}
                  t={t}
                >
                  {bookmarkFilter.label}
                </QuickPill>
              )}

              <ExportButton
                onClick={handleExportCatalog}
                busy={isExporting}
                t={t}
              />
            </div>
          </DisabledControl>

          <div className="flex-1" />

          {activeView?.kind !== "custom" && (
            <StatsBar stats={effectiveStats} t={t} />
          )}
        </div>
      </LensToolbar>

      {showInitialFeedLoading ? (
        <div
          className={cn(
            "flex-1 overflow-y-auto",
            "px-4 sm:px-6 lg:px-8",
            "pb-8",
          )}
          style={{ paddingTop: 136 }}
        >
          <div className="w-full max-w-[1500px] mx-auto">
            <PostsLoadingState
              progress={loadingProgress}
              stats={feedStats}
              t={t}
            />
          </div>
        </div>
      ) : isRefetching ? (
        <div
          className={cn(
            "flex-1 overflow-y-auto",
            "px-4 sm:px-6 lg:px-8",
            "pb-8",
          )}
          style={{ paddingTop: barHeight ? barHeight + 48 : 168 }}
        >
          <div className="w-full max-w-[1500px] mx-auto">
            <FeedRefetchingState t={t} />
          </div>
        </div>
      ) : (
        <div
          ref={feedScrollRef}
          className={cn(
            "flex-1 overflow-y-auto",
            "px-4 sm:px-6 lg:px-8",
            "pb-8",
          )}
          style={{ paddingTop: barHeight ? barHeight + 48 : 168 }}
        >
          <div className="w-full max-w-[1500px] mx-auto">
            <style>
              {`
                @keyframes feedCardIn {
                  0% {
                    opacity: 0;
                    transform: translateY(8px);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                @media (prefers-reduced-motion: reduce) {
                  .feed-card-in {
                    animation: none !important;
                  }
                }
              `}
            </style>

            {hasLoadedFeedOnce &&
            !isLoading &&
            filteredArticleCards.length === 0 ? (
              <div
                className="feed-card-in flex flex-col items-center justify-center py-20 gap-3"
                style={{ animation: "feedCardIn 320ms ease-out both" }}
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl"
                  style={{
                    background: t.inputBg,
                    border: `1px solid ${t.inputBorder}`,
                    color: t.textMuted,
                  }}
                >
                  <Sparkles size={20} />
                </div>

                <p className="text-sm" style={{ color: t.textMuted }}>
                  {articleCards.length > 0
                    ? "No posts match these filters"
                    : "No posts found"}
                </p>
              </div>
            ) : (
              <div
                ref={feedColumnsContainerRef}
                className="grid items-start gap-4"
                style={{
                  gridTemplateColumns: `repeat(${feedColumnCount}, minmax(0, 1fr))`,
                  gridAutoRows: `${MASONRY_ROW_UNIT}px`,
                  gridAutoFlow: "row dense",
                }}
              >
                {filteredArticleCards.map(({ id, article }, index) => (
                  <MasonryItem
                    key={id}
                    wide={isWideFeedCard(article)}
                    columnCount={feedColumnCount}
                    animationDelay={`${Math.min(index, 6) * 40}ms`}
                  >
                    {article.target_type === "takedowns" ? (
                      // TakedownCard is display-only and ignores onCardClick, so
                      // wrap it to open the detail sidebar on click (matching how
                      // FeedCard's root triggers handleCardClick). Inner buttons
                      // (copy / read-more) stopPropagation so they don't also open it.
                      <div
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer"
                        onClick={() => handleCardClick(id, article)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick(id, article);
                          }
                        }}
                      >
                        <TakedownCard
                          id={id}
                          article={article}
                          t={t}
                          onCardClick={handleCardClick}
                          onCardInteract={handleInteract}
                          isActive={activeCardId === id}
                        />
                      </div>
                    ) : article.target_type === "takedown_shopping" ? (
                      // Dedicated vendor-listing card; also display-only, so wrap it the
                      // same way to open the shopping detail view on click.
                      <div
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer"
                        onClick={() => handleCardClick(id, article)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick(id, article);
                          }
                        }}
                      >
                        <TakedownShoppingCard
                          id={id}
                          article={article}
                          t={t}
                          isActive={activeCardId === id}
                        />
                      </div>
                    ) : (
                      <FeedCard
                        id={id}
                        article={article}
                        t={t}
                        onCardClick={handleCardClick}
                        onCardInteract={handleInteract}
                        isActive={activeCardId === id}
                        typeBadge={
                          <>
                            <TypeBadge
                              type={
                                article.classification ||
                                article.target_type ||
                                article.type
                              }
                              t={t}
                            />
                            {article.target && (
                              <TargetBadge target={article.target} t={t} />
                            )}
                            {article.apt_name && (
                              <AptBadge aptName={article.apt_name} t={t} />
                            )}
                          </>
                        }
                      />
                    )}
                  </MasonryItem>
                ))}
              </div>
            )}

            {filteredArticleCards.length > 0 && (
              <ScrollSentinel
                onIntersect={loadMore}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                t={t}
              />
            )}
          </div>
        </div>
      )}

      {showCreateViewModal && (
        <CreateEditViewModal
          dark={dark}
          t={t}
          editingView={editingView}
          onClose={resetModal}
          onSaved={handleModalSaved}
          allTargetTypes={allTargetTypes}
        />
      )}
    </div>
  );
};

export default MainFeeds;
